import api from './api'
import type { Employee, EmployeeResponse } from '@/types/employee'

export const employeeService = {
  /**
   * Get all employees
   */
  async getEmployees(): Promise<Employee[]> {
    try {
      const response = await api.get<EmployeeResponse>('/users/employees')
      return response.data.data
    } catch (error) {
      console.error('Error fetching employees:', error)
      throw error
    }
  },

  /**
   * Get a specific employee by ID
   */
  async getEmployee(id: number): Promise<Employee> {
    try {
      const response = await api.get<{ success: boolean; data: Employee }>(`/users/${id}`)
      return response.data.data
    } catch (error) {
      console.error('Error fetching employee:', error)
      throw error
    }
  },

  /**
   * Update employee details
   */
  async updateEmployee(id: number, data: {
    first_name: string
    last_name: string
    middle_name?: string | null
    email: string
    password?: string
    password_confirmation?: string
  }): Promise<Employee> {
    try {
      const response = await api.put<{ success: boolean; data: Employee }>(`/users/${id}`, data)
      return response.data.data
    } catch (error) {
      console.error('Error updating employee:', error)
      throw error
    }
  },

  /**
   * Update employee password only
   */
  async updateEmployeePassword(id: number, data: {
    password: string
    password_confirmation: string
  }): Promise<Employee> {
    try {
      const response = await api.put<{ success: boolean; data: Employee }>(`/users/${id}/password`, data)
      return response.data.data
    } catch (error) {
      console.error('Error updating employee password:', error)
      throw error
    }
  },

  /**
   * Update user status (activate/deactivate)
   */
  async updateUserStatus(id: number, status: 0 | 1): Promise<{ user_details_id: number; status: string }> {
    try {
      const response = await api.put<{ 
        success: boolean; 
        data: { user_details_id: number; status: string } 
      }>(`/users/${id}/status`, { status })
      return response.data.data
    } catch (error) {
      console.error('Error updating user status:', error)
      throw error
    }
  },

  /**
   * Sync module access for all employees (44 months from Jan of current year)
   */
  async syncModuleAccess(): Promise<{ success: boolean; message: string; data: { synced_count: number } }> {
    try {
      const response = await api.post<{ success: boolean; message: string; data: { synced_count: number } }>('/users/sync-module-access')
      return response.data
    } catch (error: any) {
      console.error('Error syncing module access:', error)
      if (error.response?.data) {
        throw error.response.data
      }
      throw { success: false, message: 'Failed to sync module access. Please try again.' }
    }
  }
}

export default employeeService